import { Link, useLocation } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Box, Tab, Tabs } from '@mui/material';

// project imports
import menuItem from 'menu-items';
import ERoutes from 'routes/ERoutes';
import { useSelector } from 'store';
import { NavItemType } from 'types';

// Sub-pages that aren't a menu item themselves but belong under one — resolves
// the active tab for detail/edit screens reached from a tab.
const ALIASES: [string, string][] = [
    ['/crm/accounts', ERoutes.crmBoard],
    ['/businesses/invite', ERoutes.businessList],
    ['/branded-app-promo-code', ERoutes.promoCodes],
    ['/promo-code-management', ERoutes.promoCodes],
    ['/email-template', '/email-template/list'],
    ['/page-management', ERoutes.pageList],
    ['/system/queue-monitor', ERoutes.queueMonitor],
    // POGO-13862 (A2): keep the Onboarding report under the Reporting group —
    // and any deep pages reached from it — rather than bleeding the Business tab.
    ['/reporting/onboarding', ERoutes.reportingOnboarding]
];

type Resolved = { children: NavItemType[]; activeUrl: string } | null;

function resolveActive(pathname: string, role: string | undefined): Resolved {
    const groups = menuItem.items.map((group) => ({
        ...group,
        visibleChildren: (group.children || []).filter((child) => !child.roles?.length || child.roles.includes(role as string))
    }));

    // best direct match: the child whose url is the longest prefix of pathname
    let best: { children: NavItemType[]; child: NavItemType } | null = null;
    groups.forEach((group) => {
        group.visibleChildren.forEach((child) => {
            if (!child.url) return;
            if (pathname === child.url || pathname.startsWith(`${child.url}/`)) {
                if (!best || (child.url?.length || 0) > (best.child.url?.length || 0)) {
                    best = { children: group.visibleChildren, child };
                }
            }
        });
    });

    // alias fall-back for deep pages (detail/edit) with no menu item of their own
    if (!best) {
        const alias = ALIASES.find(([prefix]) => pathname.startsWith(prefix));
        if (alias) {
            const [, target] = alias;
            groups.forEach((group) => {
                const child = group.visibleChildren.find((c) => c.url === target);
                if (child) best = { children: group.visibleChildren, child };
            });
        }
    }

    if (!best) return null;
    const resolved = best as { children: NavItemType[]; child: NavItemType };
    return { children: resolved.children, activeUrl: resolved.child.url as string };
}

/**
 * Second-level navigation (POGO-12603): renders the current section's sibling
 * pages as tabs across the top of the content. The left sidebar shows only the
 * top-level groups; this bar exposes their children. Hidden for sections with a
 * single page (e.g. Dashboard, System).
 */
const SubNav = () => {
    const theme = useTheme();
    const { pathname } = useLocation();
    const { user } = useSelector((state: any) => state.auth);

    const resolved = resolveActive(pathname, user?.role);
    if (!resolved) {
        return null;
    }

    // Render one tab per unique URL. Two tabs sharing a `value` make MUI's
    // active indicator land on the wrong tab (the "off-by-one" underline), so
    // de-dupe by url and drop children with no url.
    const seen = new Set<string>();
    const tabs = resolved.children.filter((child) => {
        const url = child.url as string | undefined;
        if (!url || seen.has(url)) return false;
        seen.add(url);
        return true;
    });

    if (tabs.length <= 1) {
        return null;
    }

    // The value MUST match exactly one rendered tab, else MUI mis-places the
    // indicator; fall back to `false` (no indicator) when it doesn't.
    const activeValue = tabs.some((child) => child.url === resolved.activeUrl) ? resolved.activeUrl : false;

    return (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs
                value={activeValue}
                variant="scrollable"
                scrollButtons="auto"
                textColor="primary"
                indicatorColor="primary"
                sx={{ minHeight: 44, '& .MuiTab-root': { minHeight: 44, fontWeight: 500 } }}
            >
                {tabs.map((child) => (
                    <Tab
                        key={child.url}
                        value={child.url}
                        label={child.title}
                        component={Link}
                        to={child.url as string}
                        sx={{ color: theme.palette.text.primary }}
                    />
                ))}
            </Tabs>
        </Box>
    );
};

export default SubNav;
